/**
 * Probe for a usable WebGL context.
 *
 * The auth-page backgrounds hand a canvas straight to `new THREE.WebGLRenderer`,
 * which throws when the browser refuses a context. That throw happens inside an
 * effect-scheduled promise, so it surfaces as an *unhandled rejection* — a React
 * error boundary never sees it (boundaries only catch the render phase). Checking
 * first is therefore the only way to keep the failure off the error tracker.
 *
 * Refusals are common enough to matter: iOS Lockdown Mode, GPU driver blocklists,
 * hardened enterprise policies, and headless/virtualised clients all hit it.
 */

type CanvasFactory = () => HTMLCanvasElement | null;

/** Newest first — a hit on `webgl2` means the older names are redundant. */
const CONTEXT_NAMES = ['webgl2', 'webgl', 'experimental-webgl'] as const;

function defaultCanvasFactory(): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  return document.createElement('canvas');
}

/**
 * Returns true only if the browser actually hands back a WebGL context.
 *
 * Never throws and never returns true on the server: callers use it to decide
 * whether to mount a WebGL scene at all, so a false negative degrades to a
 * static fallback while a false positive would crash the page.
 */
export function isWebGLAvailable(createCanvas: CanvasFactory = defaultCanvasFactory): boolean {
  try {
    const canvas = createCanvas();
    if (!canvas || typeof canvas.getContext !== 'function') return false;

    for (const name of CONTEXT_NAMES) {
      // `getContext` may throw rather than return null on locked-down clients,
      // which is why the whole loop sits inside the try.
      if (canvas.getContext(name)) return true;
    }

    return false;
  } catch {
    return false;
  }
}
